'use client';

import { memo, useCallback } from 'react';

import { type Board, type Library } from '@air/api/types';

import { SubBoardsTree } from '~/components/tree/SubBoardsTree';
import { TreeItem, type TreeItemProps } from '~/components/tree/TreeItem';
import { useTreeStateContext } from '~/contexts/TreeStateContext';

export type LibraryIconProps = {
  library: Library;
};

/** Colored library icon that displays the library's icon/emoji with its background color */
const LibraryIcon = memo(({ library }: LibraryIconProps) => (
  <div
    className="flex size-6 items-center justify-center rounded text-10"
    style={{
      backgroundColor: library.color.backgroundHex,
      color: library.color.fontHex,
    }}
  >
    {library.icon || '📁'}
  </div>
));

LibraryIcon.displayName = 'LibraryIcon';

export type LibrariesTreeItemProps = {
  library: Library;
  workspaceId: string;
  onLibraryClick: (libraryId: string) => void;
  onBoardClick: (board: Board) => void;
  onBoardMouseEnter?: (board: Board) => void;
  selectedLibraryId?: string;
  selectedBoardId?: string;
  isDisabled?: (board: Board) => boolean;
};

export const LibrariesTreeItem = memo(
  ({
    library,
    workspaceId,
    onLibraryClick,
    onBoardClick,
    onBoardMouseEnter,
    selectedLibraryId,
    selectedBoardId,
    isDisabled,
  }: LibrariesTreeItemProps) => {
    const { getNodeStateById, onNodeChange } = useTreeStateContext();
    const open = getNodeStateById(library.id);
    const isSelected = selectedLibraryId === library.id;

    const handleOpenChange = useCallback<Required<TreeItemProps>['onOpenChange']>(
      (open) => {
        onNodeChange(library.id, open);
      },
      [library.id, onNodeChange],
    );

    const handleLibraryClick = useCallback(() => {
      onLibraryClick(library.id);
    }, [library.id, onLibraryClick]);

    return (
      <>
        <TreeItem
          depth={0}
          onClick={handleLibraryClick}
          onOpenChange={handleOpenChange}
          open={open}
          prefix={<LibraryIcon library={library} />}
          selected={isSelected && !selectedBoardId}
          showCaretOnHover
        >
          {library.title}
        </TreeItem>

        {open && (
          <SubBoardsTree
            depth={1}
            isDisabled={isDisabled}
            libraryId={library.id}
            onClick={onBoardClick}
            onMouseEnter={onBoardMouseEnter}
            selectedBoardId={selectedBoardId}
            workspaceId={workspaceId}
          />
        )}
      </>
    );
  },
);

LibrariesTreeItem.displayName = 'LibrariesTreeItem';
